import { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';

import type { ApiUser } from '@izimi/api';

import { snapshotHandler } from './util/snapshot';
import {
  useInvalidateCommunicationsCache,
  useInvalidateContactListCache,
  useInvalidateContactRequestsCache,
  useInvalidateDocumentsSharedFromNotaryListCache,
  useInvalidateDocumentsSharedToYouListCache,
  useInvalidateDocumentsUnSeenListCache,
  useInvalidateDocumentsVaultListCache,
  useInvalidateLastNotificationsCache,
  useInvalidateNotificationsCache,
  useInvalidateSharedItemsCache,
  useInvalidateVaultInfoCache,
  useModules,
} from '../';

type Props = {
  firestore: Firestore;
  user: ApiUser;
  onEvent?: (event: Record<string, string>) => void;
};

export const RealtimeCacheSync = ({ firestore, user, onEvent }: Props) => {
  const invalidateCommunicationsCache = useInvalidateCommunicationsCache('communications');
  const invalidateCommunicationsPreviewCache = useInvalidateCommunicationsCache('preview');
  const invalidateNotificationsCache = useInvalidateNotificationsCache();
  const invalidateLastNotificationsCache = useInvalidateLastNotificationsCache();
  const invalidateContactsCache = useInvalidateContactListCache();
  const invalidateContactRequestsCache = useInvalidateContactRequestsCache();
  const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
  const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
  const invalidateDocumentsSharedFromNotaryListCache =
    useInvalidateDocumentsSharedFromNotaryListCache();
  const invalidateDocumentsSharedToYouListCache = useInvalidateDocumentsSharedToYouListCache();
  const invalidateSharedItemsCache = useInvalidateSharedItemsCache();
  const invalidateDocumentsUnSeenListCache = useInvalidateDocumentsUnSeenListCache();
  const {
    user: { useFrontendSyncConfiguration },
  } = useModules();

  const { frontendSyncConfiguration } = useFrontendSyncConfiguration({
    enabled: Boolean(user?.id) && !user?.deactivationRequested,
  });

  const cacheInvalidationFns = useMemo(
    () => ({
      invalidateNotificationsCache,
      invalidateCommunicationsPreviewCache,
      invalidateLastNotificationsCache,
      invalidateContactsCache,
      invalidateContactRequestsCache,
      invalidateVaultInfoCache,
      invalidateDocumentsVaultListCache,
      invalidateDocumentsSharedFromNotaryListCache,
      invalidateDocumentsSharedToYouListCache,
      invalidateSharedItemsCache,
      invalidateCommunicationsCache,
      invalidateDocumentsUnSeenListCache,
    }),
    [
      invalidateNotificationsCache,
      invalidateCommunicationsPreviewCache,
      invalidateLastNotificationsCache,
      invalidateContactsCache,
      invalidateContactRequestsCache,
      invalidateVaultInfoCache,
      invalidateDocumentsVaultListCache,
      invalidateDocumentsSharedFromNotaryListCache,
      invalidateDocumentsSharedToYouListCache,
      invalidateSharedItemsCache,
      invalidateCommunicationsCache,
      invalidateDocumentsUnSeenListCache,
    ],
  );

  useEffect(() => {
    const token = frontendSyncConfiguration?.token;
    const userId = user?.id;

    if (token && userId) {
      const unSub = (async function () {
        try {
          const auth = getAuth();
          await signInWithCustomToken(auth, token);
          const docRef = doc(firestore, `users/${userId}`);
          const docSnapshot = await getDoc(docRef);

          if (!docSnapshot.exists()) {
            await setDoc(docRef, {});
          }

          return onSnapshot(docRef, (data) =>
            snapshotHandler({ data, cacheInvalidationFns, onEvent }),
          );
        } catch (error) {
          Sentry.withScope((scope) => {
            scope.setTags({
              description: 'Failed to setup firestore realtime cache sync',
            });
            Sentry.captureException(error);
          });
        }
        return null;
      })();

      return () => {
        unSub.then((fn) => {
          fn?.();
        });
      };
    }
    return undefined;
  }, [user, firestore, cacheInvalidationFns, frontendSyncConfiguration, onEvent]);

  return null;
};
